import * as yup from 'yup';
import { merge } from 'lodash-es';
import { dateField, usdField } from './common-schema-defs';
import { BaseModelWithID, baseModelWithIDFields } from './BaseModel.schema';
import { fundInvestmentTransactionFields } from './FundReserves.schema';

interface QuarterlyDateFields {
  dateFrom?: string | null;
  dateTo: string | null;
}

export function capitalAllocationResponseFields() {
  return {
    id: yup.number().required(), // fund id
    quarterlyMetrics: yup.array().of(capitalAllocationSchema()).default([]),
    summary: capitalAllocationSchema(),
  };
}

export function capitalAllocationResponseSchema() {
  return yup.object().shape(capitalAllocationResponseFields());
}
export type CapitalAllocationResponse = Omit<
  yup.InferType<ReturnType<typeof capitalAllocationResponseSchema>>,
  'quarterlyMetrics'
> & { quarterlyMetrics: CapitalAllocation[] };

export function createCapitalAllocationResponse(
  overrides: Partial<CapitalAllocationResponse> = {}
): CapitalAllocationResponse {
  return merge({ id: 0 }, capitalAllocationResponseSchema().getDefault(), overrides);
}

export function cumulativeFundMetricsFields() {
  return {
    allocatedCapitalBalance: usdField()
      .nullable()
      .default(null)
      .label('Allocated Capital Balance (cumulative)'),
    feesAndExpenses: usdField().nullable().default(null).label('Fees & Expenses (cumulative)'),
    feesAndExpensesBalance: usdField().nullable().default(null).label('Fees & Expenses Balance (cumulative)'),
    initialInvestmentsBalance: usdField()
      .nullable()
      .default(null)
      .label('Initial Investments Balance (cumulative)'),
    initialInvestmentsInvested: usdField()
      .nullable()
      .default(null)
      .label('Initial Investments Invested (cumulative)'),
    initialInvestmentsPacing: usdField()
      .nullable()
      .default(null)
      .label('Initial Investments Pacing (cumulative)'),
    initialInvestmentsPacingBalance: usdField()
      .nullable()
      .default(null)
      .label('Initial Investments Pacing Balance (cumulative)'),
    projectedFeesAndExpenses: usdField()
      .nullable()
      .default(null)
      .label('Projected Fees & Expenses (cumulative)'),
    projectedInitialInvestmentsAmount: usdField()
      .nullable()
      .default(null)
      .label('Projected Initial Investments Amount (cumulative)'),
    projectedReservesAmount: usdField()
      .nullable()
      .default(null)
      .label('Projected Reserves Amount (cumulative)'),
    reservesBalance: usdField().nullable().default(null).label('Reserves Balance (cumulative)'),
    reservesInvested: usdField().nullable().default(null).label('Reserves Invested (cumulative)'),
    reservesPacing: usdField().nullable().default(null).label('Reserves Pacing (cumulative)'),
    reservesPacingBalance: usdField().nullable().default(null).label('Reserves Pacing Balance (cumulative)'),
    totalCapitalInvested: usdField().nullable().default(null).label('Total Capital Invested (cumulative)'),
    totalProjectedInitialInvestmentsAmount: usdField()
      .nullable()
      .default(null)
      .label('Total Projected Initial Investments Amount (cumulative)'),
    totalProjectedReservesAmount: usdField()
      .nullable()
      .default(null)
      .label('Total Projected Reserves Amount (cumulative)'),
  };
}

export function capitalAllocationFields() {
  return {
    allocatedCapitalBalance: usdField().nullable().default(null), // sum of Initial Investments Balance + Reserves Balance + Fees & Expenses Balance
    cumulativeMetrics: yup.object(cumulativeFundMetricsFields()).nullable().default(null),
    dateFrom: dateField().nullable().default(null),
    dateTo: dateField().nullable().default(null),
    feesAndExpenses: usdField().nullable().default(null).label('Fees & Expenses'),
    feesAndExpensesBalance: usdField().nullable().default(null).label('Fees & Expenses Balance'), // relative to allocation
    initialInvestmentsBalance: usdField().nullable().default(null), // Initial Investment Allocation - Initial Investments Invested
    initialInvestmentsInvested: usdField().nullable().default(null), // cumulative sum of real initial investments
    initialInvestmentsPacing: usdField().nullable().default(null), // cumulative sum of real and future initial investments
    initialInvestmentsPacingBalance: usdField().nullable().default(null), // Initial Investment Allocation - Initial Investments Pacing
    period: yup.string().default(''), // e.g. 'Q2 2021'
    projectedFeesAndExpenses: usdField().nullable().default(null).label('Projected Fees & Expenses'), //  sum of future fees and expenses
    projectedFeesAndExpensesBalance: usdField()
      .nullable()
      .default(null)
      .label('Projected Fees & Expenses Balance'), // Fees & Expenses Allocation - Fees & Expenses Projected
    projectedFeesAndExpensesTransaction: projectedFeesAndExpensesTransactionSchema()
      .label('Projected Fees & Expenses Transaction')
      .nullable()
      .default(null), // user-entered, projected fees and expenses
    projectedReservesAmount: usdField().nullable().default(null),
    reservesBalance: usdField().nullable().default(null),
    reservesInvested: usdField().nullable().default(null),
    reservesPacing: usdField().nullable().default(null),
    reservesPacingBalance: usdField().nullable().default(null),
    totalCapitalInvested: usdField().nullable().default(null), // Initial Investments Invested + Reserves Invested + Fees & Expenses
  };
}

export function capitalAllocationSchema() {
  return yup.object().shape(capitalAllocationFields());
}

export type CapitalAllocation = Omit<
  yup.InferType<ReturnType<typeof capitalAllocationSchema>>,
  'dateTo' | 'dateFrom'
> &
  QuarterlyDateFields;

export function createCapitalAllocation(overrides: Partial<CapitalAllocation> = {}): CapitalAllocation {
  return merge({}, capitalAllocationSchema().getDefault(), overrides);
}

export function projectedFeesAndExpensesTransactionFields() {
  const { id, ...baseFields } = baseModelWithIDFields();
  const { amount, date } = fundInvestmentTransactionFields();
  return {
    ...baseFields,
    amount: amount.label('Projected Fees And Expenses'),
    date,
    fundId: yup.number().required(),
    id: id.nullable(),
    name: yup.string(),
  };
}

export function projectedFeesAndExpensesTransactionSchema() {
  return yup.object().shape(projectedFeesAndExpensesTransactionFields());
}

export function createProjectedFeesAndExpensesTransaction(
  overrides: Partial<ProjectedFeesAndExpensesTransaction> = {}
): ProjectedFeesAndExpensesTransaction {
  return merge({ fundId: 0 }, projectedFeesAndExpensesTransactionSchema().getDefault(), overrides);
}

export type ProjectedFeesAndExpensesTransaction = Omit<
  yup.InferType<ReturnType<typeof projectedFeesAndExpensesTransactionSchema>>,
  'createdAt' | 'date' | 'updatedAt'
> & { date: string } & Pick<BaseModelWithID, 'createdAt' | 'updatedAt'>;
